import LegalPageLayout from '@/components/legal/LegalPageLayout'
import SEO from '@/components/layout/SEO'

export default function TermsPage() {
  return (
    <LegalPageLayout title="Terms & Conditions" lastUpdated="September 17, 2026">
      <SEO
        title="Terms & Conditions"
        description="Read the terms and conditions for Still Waters Academy, covering our AI education services, student accounts, parent supervision, and subscription policies."
      />
      <section>
        <p>
          Welcome to Still Waters Academy. These Terms & Conditions (<strong>"Terms"</strong>) govern your access to and use of our educational academy, learning management systems, interactive curriculum, and AI tutoring services.
          By accessing or using our platform, you agree to be bound by these Terms. If you do not agree to these Terms, please do not access or use the Service.
        </p>
        <blockquote>
          Still Waters Academy is owned and operated by <strong>Still Waters LLC</strong>, a company registered in the State of Wyoming, USA, doing business as <strong>Still Waters Academy</strong> (<strong>"Still Waters"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>).
        </blockquote>
      </section>

      <section>
        <h2>1. Eligibility, Family Accounts & Minors</h2>
        <p>
          Still Waters Academy provides educational instruction designed for learners from age 8 through adulthood. Because our curriculum serves minors:
        </p>
        <ul>
          <li><strong>Parent / Guardian Requirement:</strong> Any learner under 18 years of age (and specifically under 13) may only access the platform through a student account created and supervised by an adult parent, legal guardian, or verified educational institution.</li>
          <li><strong>Parental Responsibility:</strong> Parents and legal guardians who register family accounts represent that they have legal authority to enroll their participating children and agree to supervise their educational participation.</li>
          <li><strong>Individual Adult Learners:</strong> Individuals aged 18 or older may register independent individual learner accounts directly.</li>
          <li><strong>Account Security:</strong> You are responsible for safeguarding your login credentials and for all activities that occur under your family or student accounts.</li>
        </ul>
      </section>

      <section>
        <h2>2. Educational Services & AI Tutoring</h2>
        <p>
          Still Waters Academy provides structured modular curricula across classical subjects, elite disciplines, rhetoric, logic, mathematics, sciences, and elective faith studies.
        </p>
        <ul>
          <li><strong>AI Educational Tutoring:</strong> Lessons, discussions, practice exercises, and formative evaluations are delivered through conversational AI tutors designed to explain concepts adaptively according to the learner's age bracket.</li>
          <li><strong>Educational Aid, Not Human Certification:</strong> While our curricula are rigorous and adhere to classical mastery standards, Still Waters Academy provides supplemental and homeschooling instruction. It does not confer accredited state diplomas unless explicitly stated in partnership with a recognized accredited school district.</li>
          <li><strong>Parental Oversight Dashboards:</strong> Parents have direct access to their child's completed lessons, quiz scores, strengths, and areas for improvement. Lessons are intended to foster independent student inquiry under ongoing parental mentorship.</li>
        </ul>
      </section>

      <section>
        <h2>3. Institutional, School & Church Hubs</h2>
        <p>
          Schools, churches, and homeschool co-ops (<strong>"Institutions"</strong>) may establish multi-seat educational facilities. Authorized administrators are responsible for:
        </p>
        <ul>
          <li>Obtaining requisite parental permissions before enrolling minor students.</li>
          <li>Managing member assignments and monitoring collective cohort mastery.</li>
          <li>Ensuring institutional use adheres to applicable local educational privacy standards.</li>
        </ul>
      </section>

      <section>
        <h2>4. Acceptable Conduct & Student Safety</h2>
        <p>
          To preserve an uplifting, respectful, and academically rigorous environment, all users agree not to:
        </p>
        <ul>
          <li>Submit abusive, defamatory, obscene, harassing, or sexually explicit content during lesson dialogues.</li>
          <li>Attempt to manipulate the AI tutor to generate harmful, fraudulent, or unsafe material.</li>
          <li>Reverse-engineer, copy, scrape, or extract platform curriculum content, module plans, or underlying system prompts without prior written authorization.</li>
          <li>Share account access outside the designated household or authorized institution.</li>
        </ul>
      </section>

      <section>
        <h2>5. Subscriptions, Billing & Cancellations</h2>
        <p>
          Access to Still Waters Academy curricula is provided via recurring subscription plans or designated institutional licenses. All financial transactions are managed via Stripe.
        </p>
        <ul>
          <li><strong>Recurring Billing:</strong> Subscriptions are billed in advance on a monthly or annual cycle and renew automatically unless cancelled before the renewal date.</li>
          <li><strong>Cancellation Policy:</strong> You may cancel your subscription at any time via the Parent Dashboard or Subscription portal. Access to plan-covered courses continues through the end of the paid billing period.</li>
          <li><strong>Seat & Child Limits:</strong> Subscriptions include access based on the purchased plan tier and enrolled student count. Sibling discounts and promotional codes must be applied at initial checkout where permitted.</li>
          <li><strong>Refunds:</strong> Because course access and AI mentorship are provisioned immediately upon purchase, subscription fees are generally non-refundable, except where required by applicable consumer law.</li>
        </ul>
      </section>

      <section>
        <h2>6. Intellectual Property</h2>
        <p>
          All curriculum materials, lesson frameworks, rubrics, audio, logos, software, and proprietary learning methodologies are the intellectual property of Still Waters LLC. Users are granted a limited, personal, non-exclusive, non-transferable license to utilize the materials for home education and personal study.
        </p>
      </section>

      <section>
        <h2>7. Disclaimers & Limitation of Liability</h2>
        <p>
          The Service is provided on an "as-is" and "as-available" basis. While we strive for absolute pedagogical rigor:
        </p>
        <ul>
          <li><strong>Generative AI Limitations:</strong> Large language models can occasionally generate imperfect or inaccurate responses. We encourage students and parents to review critical concepts and reference standard primary texts.</li>
          <li><strong>Not Crisis or Emergency Services:</strong> The Service is purely educational. If a child or user is experiencing a health, safety, or mental emergency, contact 911 or local emergency authorities immediately.</li>
          <li><strong>Liability Cap:</strong> To the maximum extent permitted by Wyoming law, Still Waters LLC's aggregate liability for any claim arising from the Service shall not exceed the total fees paid by you in the twelve (12) months preceding the claim.</li>
        </ul>
      </section>

      <section>
        <h2>8. Governing Law & Dispute Resolution</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the <strong>State of Wyoming, USA</strong>, without giving effect to conflict of laws principles. Any legal action or dispute arising hereunder shall be subject to the exclusive jurisdiction of the state and federal courts located in Wyoming.
        </p>
      </section>

      <section>
        <h2>9. Contact Us</h2>
        <p>
          If you have questions regarding these Terms or our educational platform policies, please contact us:
        </p>
        <p>
          <strong>Email:</strong> <a href="mailto:support@stillwatersacademy.com">support@stillwatersacademy.com</a><br />
          <strong>Entity:</strong> Still Waters LLC (Wyoming, USA) dba Still Waters Academy<br />
          <strong>Address:</strong> 30 N Gould St Ste R, Sheridan, WY 82801, USA
        </p>
      </section>
    </LegalPageLayout>
  )
}
